import React, { useState, useEffect, useCallback } from "react"
import { Search, UserPlus } from "lucide-react"
import { Input } from "src/components/ui/input"
import { Button } from "src/components/ui/button"
import { useAuth } from "src/contexts/AuthContext"
import { NoAccess } from "src/components/shared/NoAccess"
import { Can } from "src/components/shared/Can"
import { useMembers } from "src/features/members/hooks/useMembers"
import { MembersTable } from "src/features/members/components/MembersTable"
import { MemberDrawer } from "src/features/members/components/MemberDrawer"
import { InviteMemberModal } from "src/features/members/components/InviteMemberModal"

function useDebounce(value, delay = 300) {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return debounced
}

export default function MembersPage() {
  const { can } = useAuth()
  const canRead = can("memberships", "read")

  const [searchRaw, setSearch] = useState("")
  const q = useDebounce(searchRaw)
  const [editing, setEditing] = useState(null)
  const [inviteModalOpen, setInviteModalOpen] = useState(false)

  const { data, loading, fetch } = useMembers()

  const reload = useCallback(() => fetch({ page: 1, per_page: 100, q }), [fetch, q])
  useEffect(() => { if (canRead) reload() }, [q, canRead])

  const members = data?.memberships ?? []
  const total = data?.meta?.total

  if (!canRead) {
    return <NoAccess message="You don't have permission to view members of this organization." />
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Members</h2>
          <p className="text-[13px] text-slate-500">
            {total != null ? `${total} member${total !== 1 ? "s" : ""}` : "Manage who can access this organization"}
          </p>
        </div>

        <Can resource="memberships" action="invite">
          <Button
            variant="default"
            size="sm"
            className="gap-2"
            onClick={() => setInviteModalOpen(true)}
          >
            <UserPlus className="h-4 w-4" />
            Invite Member
          </Button>
        </Can>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search
          style={{ left: "0.75rem" }}
          className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
        />
        <Input
          style={{ paddingLeft: "2.5rem" }}
          placeholder="Search team members"
          value={searchRaw}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Table */}
      <MembersTable
        members={members}
        loading={loading}
        hasFilters={!!q}
        onEdit={setEditing}
      />

      <MemberDrawer
        open={!!editing}
        member={editing}
        onClose={() => setEditing(null)}
        onSuccess={reload}
      />

      <InviteMemberModal
        open={inviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
        onSuccess={reload}
      />
    </div>
  )
}
